'use client';
import React, { useState, useEffect, useCallback } from 'react';
import { AuthenticationManager } from '@benzinga/session';
import { Watchlist, WatchlistManager, WatchlistManagerEvent, WatchlistSymbol } from '@benzinga/watchlist-manager';
import Hooks from '@benzinga/hooks';
import { SessionContext } from '@benzinga/session-context';
import { useIsUserLoggedIn } from '@benzinga/user-context';
import { WatchlistsDropdown } from './WatchlistsDropdown';
import { ButtonVariant } from '@benzinga/core-ui';
import { StockSymbol } from '@benzinga/session';

export interface AddAllToWatchlistProps {
  symbols: StockSymbol[];
  variant?: 'default' | 'compact';
  buttonText?: string;
  buttonVariant?: ButtonVariant;
  showIcon?: boolean;
}

export const AddAllToWatchlist: React.FC<AddAllToWatchlistProps> = ({
  buttonText,
  buttonVariant,
  showIcon,
  symbols,
  variant = 'default',
}) => {
  const session = React.useContext(SessionContext);
  const [watchlistManager] = useState(session.getManager(WatchlistManager));
  const [authManager] = useState(session.getManager(AuthenticationManager));
  const [watchlists, setWatchlists] = useState(watchlistManager.getStoredWatchlists());
  const [, setWatchlistInProcess] = useState(false);
  const [watchlistDebounce, setWatchlistDebounce] = useState<number | null>(null);
  const isLoggedIn = useIsUserLoggedIn();

  const actionWithDebounce = useCallback(
    (callback: () => void, ms = 500) => {
      if (watchlistDebounce) {
        clearTimeout(watchlistDebounce);
      }

      const watchlistActionDebounce = window.setTimeout(() => {
        callback();
      }, ms);

      setWatchlistDebounce(watchlistActionDebounce);
    },
    [watchlistDebounce],
  );

  const onSymbolAdded = useCallback(
    (watchlist: Watchlist | undefined) => {
      if (watchlist === undefined) return;
      setWatchlistInProcess(true);
      const watchlistOrigin = { ...watchlist, symbols: [...watchlist.symbols] };
      actionWithDebounce(() => {
        watchlistManager.addTickersToWatchlist(watchlistOrigin, symbols).then(() => {
          setWatchlistInProcess(false);
        });
      });
    },
    [actionWithDebounce, watchlistManager, symbols],
  );

  const onSymbolRemove = useCallback(
    async (watchlist: Watchlist | undefined) => {
      if (watchlist === undefined) return;
      setWatchlistInProcess(true);
      const toBeRemovedFromWatchlist: WatchlistSymbol[] = [];
      symbols.forEach(symbol => {
        watchlist.symbols.find(watchitem => {
          if (watchitem.symbol === symbol) {
            toBeRemovedFromWatchlist.push(watchitem);
          }
        });
      });

      actionWithDebounce(() => {
        watchlistManager.removeTickersFromWatchlist(watchlist, toBeRemovedFromWatchlist).then(() => {
          setWatchlistInProcess(false);
        });
      });
    },
    [actionWithDebounce, watchlistManager, symbols],
  );

  Hooks.useSubscriber(watchlistManager, (event: WatchlistManagerEvent) => {
    switch (event.type) {
      case 'watchlist:updated_watchlists':
        setWatchlists(event.watchlists);
        break;
      default:
        break;
    }
  });

  const onButtonClick = useCallback(() => {
    if (!isLoggedIn) {
      authManager.requestAuthorization();
    }
  }, [authManager, isLoggedIn]);

  useEffect(() => {
    if (isLoggedIn) {
      (async () => {
        const response = await watchlistManager.getWatchlists();

        if (response.err) {
          setWatchlists([]);
        } else {
          setWatchlists(response.ok ?? []);
        }
      })();
    } else {
      setWatchlists([]);
    }
  }, [isLoggedIn, watchlistManager]);

  return (
    <WatchlistsDropdown
      buttonText={buttonText || 'Add All To Watchlist +'}
      buttonVariant={buttonVariant}
      onAddedSymbolToWatchlist={onSymbolAdded}
      onClick={onButtonClick}
      onRemovedSymbolFromWatchlist={onSymbolRemove}
      showEmptyList={!!isLoggedIn}
      showIcon={showIcon}
      symbols={symbols}
      variant={variant}
      watchlists={watchlists}
    />
  );
};
